/**
 * WorkspaceSettingsPanel
 *
 * A right-side slide-out panel for workspace settings.
 * Allows renaming, editing description (with AI auto-generation when blank),
 * editing date range, managing jurisdictions, and deleting the workspace.
 *
 * Admin-only: only shown when the user is a SYSTEM_ADMIN, ORG_OWNER,
 * WORKSPACE_ADMIN, or the workspace owner.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Settings, Trash2, X, Sparkles, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { apiFetch } from '@/lib/apiFetch';
import type { Workspace } from '@/types/database';

interface JurisdictionInfo {
  id: string;
  name: string;
  level?: string;
}

interface WorkspaceSettingsPanelProps {
  workspace: Workspace;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  jurisdictionNames?: string[];
  jurisdictionIds?: string[];
}

export const WorkspaceSettingsPanel: React.FC<WorkspaceSettingsPanelProps> = ({
  workspace,
  open,
  onClose,
  onSaved,
  jurisdictionNames = [],
  jurisdictionIds = [],
}) => {
  const navigate = useNavigate();
  const { updateWorkspace, deleteWorkspace } = useWorkspaces();

  const [name, setName] = useState(workspace.name);
  const [description, setDescription] = useState(workspace.description ?? '');

  const resolvedIds =
    jurisdictionIds.length > 0
      ? jurisdictionIds
      : (workspace.jurisdiction_ids ??
        (workspace.jurisdiction_id ? [workspace.jurisdiction_id] : []));

  const [jurisdictionIdsState, setJurisdictionIds] =
    useState<string[]>(resolvedIds);

  const jurisdictionNamesMap = useMemo(() => {
    const m = new Map<string, string>();
    for (let i = 0; i < jurisdictionIdsState.length; i++) {
      m.set(
        jurisdictionIdsState[i],
        jurisdictionNames[i] ?? jurisdictionIdsState[i].slice(0, 8)
      );
    }
    return m;
  }, [jurisdictionIdsState, jurisdictionNames]);

  const getNameForId = useCallback(
    (id: string) => jurisdictionNamesMap.get(id) ?? id.slice(0, 8),
    [jurisdictionNamesMap]
  );

  const formatDate = (s: string | null | undefined) =>
    s ? s.slice(0, 10) : '';

  const [dateFrom, setDateFrom] = useState(formatDate(workspace.date_from));
  const [dateTo, setDateTo] = useState(formatDate(workspace.date_to));

  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Jurisdiction search
  const [jurSearch, setJurSearch] = useState('');
  const [jurResults, setJurResults] = useState<JurisdictionInfo[]>([]);
  const [jurSearching, setJurSearching] = useState(false);

  // Sync fields when workspace prop changes
  useEffect(() => {
    setName(workspace.name);
    setDescription(workspace.description ?? '');
    setDateFrom(formatDate(workspace.date_from));
    setDateTo(formatDate(workspace.date_to));
    const ids =
      workspace.jurisdiction_ids ??
      (workspace.jurisdiction_id ? [workspace.jurisdiction_id] : []);
    setJurisdictionIds(ids);
  }, [workspace]);

  // Trap body scroll while panel is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  /** Search jurisdictions via the entitled search endpoint */
  const searchJurisdictions = useCallback(async (q: string) => {
    if (q.length < 2) {
      setJurResults([]);
      return;
    }
    setJurSearching(true);
    try {
      const res = await apiFetch(
        `/api/jurisdictions/search?q=${encodeURIComponent(q)}&limit=10`
      );
      if (res.ok) {
        const json = await res.json();
        setJurResults(
          (json?.data ?? []).map(
            (j: { id: string; name: string; level: string }) => ({
              id: j.id,
              name: j.name,
              level: j.level,
            })
          )
        );
      }
    } catch {
      /* ignore */
    } finally {
      setJurSearching(false);
    }
  }, []);

  /** Add a jurisdiction by ID and name */
  const addJurisdiction = (jur: JurisdictionInfo) => {
    if (jurisdictionIdsState.includes(jur.id)) return;
    setJurisdictionIds((prev) => [...prev, jur.id]);
    jurisdictionNamesMap.set(jur.id, jur.name);
    setJurSearch('');
    setJurResults([]);
  };

  /** Remove a jurisdiction by ID */
  const removeJurisdiction = (id: string) => {
    setJurisdictionIds((prev) => prev.filter((jid) => jid !== id));
  };

  /** Ask the AI to write a 1–2 sentence workspace description */
  const generateDescription = async (workspaceName: string) => {
    setGenerating(true);
    setSaveError(null);
    try {
      const jurNamesArr = jurisdictionIdsState.map((id) => getNameForId(id));
      const jurisContext =
        jurNamesArr.length > 0
          ? `It covers the following jurisdictions: ${jurNamesArr.join(', ')}.`
          : '';
      const res = await apiFetch('/api/ai/complete', {
        method: 'POST',
        body: JSON.stringify({
          model_id: 'gpt-4o-mini',
          messages: [
            {
              role: 'user',
              content:
                `Write a concise 1–2 sentence description for a government records ` +
                `research workspace named "${workspaceName}". ${jurisContext} ` +
                `The description should explain what this workspace is used for. ` +
                `Reply with only the description text — no quotes, no labels.`,
            },
          ],
        }),
      });
      if (!res.ok) throw new Error('AI generation failed');
      const json = await res.json();
      const generated: string = json?.data?.content ?? json?.data ?? '';
      if (generated) setDescription(generated.trim());
    } catch {
      setSaveError(
        'Could not generate description — please type one manually.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    setSaving(true);
    setSaveError(null);

    let finalDescription = description.trim();

    // Auto-generate description when left blank
    if (!finalDescription) {
      try {
        setGenerating(true);
        const jurNamesArr = jurisdictionIdsState.map((id) => getNameForId(id));
        const jurisContext =
          jurNamesArr.length > 0 ? `It covers: ${jurNamesArr.join(', ')}.` : '';
        const res = await apiFetch('/api/ai/complete', {
          method: 'POST',
          body: JSON.stringify({
            model_id: 'gpt-4o-mini',
            messages: [
              {
                role: 'user',
                content:
                  `Write a concise 1–2 sentence description for a government records ` +
                  `research workspace named "${trimmedName}". ${jurisContext} ` +
                  `Reply with only the description text — no quotes, no labels.`,
              },
            ],
          }),
        });
        if (res.ok) {
          const json = await res.json();
          finalDescription = (json?.data?.content ?? json?.data ?? '').trim();
          if (finalDescription) setDescription(finalDescription);
        }
      } catch {
        /* non-fatal — save with empty description */
      } finally {
        setGenerating(false);
      }
    }

    try {
      const updates: Partial<Workspace> = {};
      if (trimmedName !== workspace.name) updates.name = trimmedName;
      if (finalDescription !== (workspace.description ?? ''))
        updates.description = finalDescription || null;

      const currDateFrom = workspace.date_from?.slice(0, 10) ?? '';
      const currDateTo = workspace.date_to?.slice(0, 10) ?? '';
      if (dateFrom !== currDateFrom)
        updates.date_from = dateFrom ? new Date(dateFrom).toISOString() : null;
      if (dateTo !== currDateTo)
        updates.date_to = dateTo ? new Date(dateTo).toISOString() : null;

      const currIds =
        workspace.jurisdiction_ids ??
        (workspace.jurisdiction_id ? [workspace.jurisdiction_id] : []);
      const newIds =
        jurisdictionIdsState.length > 0 ? jurisdictionIdsState : [];
      if (JSON.stringify(currIds.sort()) !== JSON.stringify(newIds.sort())) {
        if (newIds.length === 1) {
          (updates as Record<string, unknown>).jurisdiction_id = newIds[0];
          (updates as Record<string, unknown>).jurisdiction_ids = null;
        } else if (newIds.length > 1) {
          (updates as Record<string, unknown>).jurisdiction_ids = newIds;
          (updates as Record<string, unknown>).jurisdiction_id = null;
        } else {
          (updates as Record<string, unknown>).jurisdiction_id = null;
          (updates as Record<string, unknown>).jurisdiction_ids = null;
        }
      }

      if (Object.keys(updates).length > 0) {
        await updateWorkspace(workspace.id, updates);
      }

      onSaved();
      onClose();
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to save changes'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteWorkspace(workspace.id);
      navigate('/workspaces');
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to delete workspace'
      );
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  const isBusy = saving || generating;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col
                   bg-white shadow-2xl transition-transform duration-300"
        role="dialog"
        aria-modal="true"
        aria-label="Workspace settings"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-gray-600" />
            <h2 className="text-base font-semibold text-gray-900">
              Workspace Settings
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-gray-500"
            onClick={onClose}
            aria-label="Close settings"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-6">
          <form onSubmit={handleSave} className="space-y-4">
            {/* Name */}
            <div className="space-y-1.5">
              <Label htmlFor="ws-name">Workspace Name</Label>
              <Input
                id="ws-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Houston Planning Research"
                required
                disabled={isBusy}
                className="bg-white text-black placeholder:text-gray-400"
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="ws-desc">
                  Description
                  <span className="ml-1 text-xs font-normal text-gray-400">
                    (auto-generated if blank)
                  </span>
                </Label>
                <button
                  type="button"
                  onClick={() =>
                    void generateDescription(name.trim() || workspace.name)
                  }
                  disabled={isBusy}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800
                             disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Generate description with AI"
                >
                  <Sparkles className="h-3 w-3" />
                  {generating ? 'Generating…' : 'Generate with AI'}
                </button>
              </div>
              <Textarea
                id="ws-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this workspace is used for…"
                rows={3}
                disabled={isBusy}
                className="resize-none text-sm bg-white text-black placeholder:text-gray-400"
              />
            </div>

            {/* Date Range */}
            <div className="space-y-1.5">
              <Label>Date Range</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label
                    htmlFor="ws-date-from"
                    className="text-xs text-gray-500"
                  >
                    From
                  </Label>
                  <Input
                    id="ws-date-from"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    disabled={isBusy}
                    className="bg-white text-black"
                  />
                </div>
                <div>
                  <Label htmlFor="ws-date-to" className="text-xs text-gray-500">
                    To
                  </Label>
                  <Input
                    id="ws-date-to"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    disabled={isBusy}
                    className="bg-white text-black"
                  />
                </div>
              </div>
            </div>

            {/* Jurisdictions */}
            <div className="space-y-1.5">
              <Label>Jurisdictions</Label>

              {/* Current list */}
              {jurisdictionIdsState.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {jurisdictionIdsState.map((jid) => {
                    const jname = getNameForId(jid);
                    return (
                      <span
                        key={jid}
                        className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700"
                      >
                        {jname}
                        <button
                          type="button"
                          onClick={() => removeJurisdiction(jid)}
                          className="ml-0.5 rounded-full p-0.5 hover:bg-blue-200 transition-colors"
                          disabled={isBusy}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Search / Add */}
              <div className="relative">
                <div className="flex gap-1">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                    <Input
                      value={jurSearch}
                      onChange={(e) => {
                        setJurSearch(e.target.value);
                        void searchJurisdictions(e.target.value);
                      }}
                      placeholder="Search jurisdictions to add…"
                      className="pl-8 text-sm bg-white text-black placeholder:text-gray-400"
                      disabled={isBusy}
                    />
                  </div>
                </div>
                {jurResults.length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    {jurResults.map((j) => (
                      <button
                        key={j.id}
                        type="button"
                        onClick={() => addJurisdiction(j)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                      >
                        <span className="flex-1 truncate">{j.name}</span>
                        {j.level && (
                          <span className="shrink-0 text-xs text-gray-400 capitalize">
                            {j.level}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {jurSearching && (
                  <p className="mt-1 text-xs text-gray-400">Searching…</p>
                )}
              </div>
            </div>

            {saveError && <p className="text-sm text-red-600">{saveError}</p>}

            <Button
              type="submit"
              className="w-full"
              disabled={isBusy || !name.trim()}
            >
              {saving
                ? 'Saving…'
                : generating
                  ? 'Generating description…'
                  : 'Save Changes'}
            </Button>
          </form>

          {/* Divider */}
          <div className="my-6 border-t" />

          {/* Workspace metadata */}
          <div className="space-y-3 text-sm">
            <p className="font-medium text-gray-700">Details</p>
            <div className="space-y-1 text-gray-500">
              <div className="flex justify-between">
                <span>Kind</span>
                <span className="font-mono text-xs">{workspace.kind}</span>
              </div>
              <div className="flex justify-between">
                <span>Scope</span>
                <span className="font-mono text-xs">{workspace.scope}</span>
              </div>
              <div className="flex justify-between">
                <span>Created</span>
                <span>
                  {new Date(workspace.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Danger zone */}
          <div className="mt-8">
            <p className="mb-3 text-sm font-bold text-red-600 text-center uppercase tracking-wide">
              Danger Zone
            </p>
            {!confirmDelete ? (
              <Button
                className="w-full bg-red-600 text-white hover:bg-red-700 font-semibold"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Workspace
              </Button>
            ) : (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-3">
                <p className="text-sm text-red-800">
                  Delete <span className="font-semibold">{workspace.name}</span>
                  ? This cannot be undone.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 bg-red-600 text-white hover:bg-red-700 font-semibold"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting…' : 'Yes, Delete'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
